import { useI18n } from '@affine/i18n';
import {
  evaluateFormula,
  type Formula,
  FormulaSchema,
} from '@streamlain/view-config';
import { type FC, useEffect, useMemo, useState } from 'react';

import { BooleanToggle } from '../controls/boolean-toggle';
import { ColumnPicker } from '../controls/column-picker';
import type { ContentDbColumn } from '../utils/get-content-db-columns';
import * as css from './seo.css';
import type { SectionProps } from './types';

interface SeoSectionProps extends SectionProps {
  columns: ReadonlyArray<ContentDbColumn>;
}

const SLUG_TYPES = ['text'] as const;
const META_TYPES = ['text', 'rich-text'] as const;
const OG_TYPES = ['attachment'] as const;
const CANONICAL_TYPES = ['text'] as const;

export const SeoSection: FC<SeoSectionProps> = ({ row, setField, columns }) => {
  const t = useI18n();
  const warnMissingSlug = row.seoRobotsIndex && row.seoSlugCol === null;
  const formulaContext = useMemo(
    () => ({
      row: Object.fromEntries(columns.map(column => [column.name, column.id])),
      view: { title: row.name },
    }),
    [columns, row.name]
  );

  return (
    <>
      <ColumnPicker
        label={t['com.streamlain.customApps.viewConfig.seo.slug.label']()}
        data-sl-field="seoSlugCol"
        description={t['com.streamlain.customApps.viewConfig.seo.slug.desc']()}
        value={row.seoSlugCol}
        onChange={v => setField('seoSlugCol', v)}
        columns={columns}
        acceptedTypes={SLUG_TYPES}
      />
      <ColumnPicker
        label={t['com.streamlain.customApps.viewConfig.seo.meta.label']()}
        data-sl-field="seoMetaCol"
        description={t['com.streamlain.customApps.viewConfig.seo.meta.desc']()}
        value={row.seoMetaCol}
        onChange={v => setField('seoMetaCol', v)}
        columns={columns}
        acceptedTypes={META_TYPES}
      />
      <ColumnPicker
        label={t['com.streamlain.customApps.viewConfig.seo.og.label']()}
        data-sl-field="seoOgCol"
        description={t['com.streamlain.customApps.viewConfig.seo.og.desc']()}
        value={row.seoOgCol}
        onChange={v => setField('seoOgCol', v)}
        columns={columns}
        acceptedTypes={OG_TYPES}
      />
      <ColumnPicker
        label={t['com.streamlain.customApps.viewConfig.seo.canonical.label']()}
        data-sl-field="seoCanonicalCol"
        description={t[
          'com.streamlain.customApps.viewConfig.seo.canonical.desc'
        ]()}
        value={row.seoCanonicalCol}
        onChange={v => setField('seoCanonicalCol', v)}
        columns={columns}
        acceptedTypes={CANONICAL_TYPES}
      />
      <BooleanToggle
        label={t['com.streamlain.customApps.viewConfig.seo.robots.label']()}
        data-sl-field="seoRobotsIndex"
        value={row.seoRobotsIndex}
        onChange={v => setField('seoRobotsIndex', v)}
      />
      <FormulaTextarea
        label={t['com.streamlain.customApps.viewConfig.seo.titleFormula']()}
        field="seoTitleFormula"
        value={row.seoTitleFormula ?? null}
        columns={columns}
        previewContext={formulaContext}
        placeholder={JSON.stringify(
          { type: 'path', path: ['row', 'title'] },
          null,
          2
        )}
        onChange={formula => setField('seoTitleFormula', formula)}
      />
      <FormulaTextarea
        label={t[
          'com.streamlain.customApps.viewConfig.seo.descriptionFormula'
        ]()}
        field="seoDescriptionFormula"
        value={row.seoDescriptionFormula ?? null}
        columns={columns}
        previewContext={formulaContext}
        placeholder={JSON.stringify(
          { type: 'path', path: ['row', 'description'] },
          null,
          2
        )}
        onChange={formula => setField('seoDescriptionFormula', formula)}
      />
      <FormulaTextarea
        label={t['com.streamlain.customApps.viewConfig.seo.ogImageFormula']()}
        field="seoOgImageFormula"
        value={row.seoOgImageFormula ?? null}
        columns={columns}
        previewContext={formulaContext}
        placeholder={JSON.stringify(
          { type: 'path', path: ['row', 'cover'] },
          null,
          2
        )}
        onChange={formula => setField('seoOgImageFormula', formula)}
      />
      <div className={css.formulaHint}>
        {t['com.streamlain.customApps.viewConfig.seo.formulaContext']({
          context: JSON.stringify(formulaContext),
        })}
      </div>
      {warnMissingSlug ? (
        <div
          className={css.warning}
          role="alert"
          data-sl-role="seo-slug-warning"
        >
          {t['com.streamlain.customApps.viewConfig.seo.warnMissingSlug']()}
        </div>
      ) : null}
    </>
  );
};

function FormulaTextarea({
  label,
  field,
  value,
  columns,
  previewContext,
  placeholder,
  onChange,
}: {
  label: string;
  field: 'seoTitleFormula' | 'seoDescriptionFormula' | 'seoOgImageFormula';
  value: Formula | null;
  columns: ReadonlyArray<ContentDbColumn>;
  previewContext: Record<string, unknown>;
  placeholder: string;
  onChange: (formula: Formula | null) => void;
}) {
  const t = useI18n();
  const serialized = value ? JSON.stringify(value, null, 2) : '';
  const [raw, setRaw] = useState(serialized);
  const [error, setError] = useState<string | null>(null);
  const parsedFormula = useMemo(() => {
    if (!raw.trim()) return null;
    try {
      const parsed = JSON.parse(raw);
      const result = FormulaSchema.safeParse(parsed);
      return result.success ? result.data : null;
    } catch {
      return null;
    }
  }, [raw]);
  const preview = useMemo(() => {
    if (!parsedFormula) return null;
    try {
      const result = evaluateFormula(parsedFormula, { data: previewContext });
      return result === null || result === undefined ? '' : String(result);
    } catch (err) {
      return err instanceof Error
        ? err.message
        : t['com.streamlain.customApps.viewConfig.seo.previewFailed']();
    }
  }, [parsedFormula, previewContext, t]);

  useEffect(() => {
    setRaw(serialized);
    setError(null);
  }, [serialized]);

  return (
    <label className={css.formulaField} data-sl-field={field}>
      <span className={css.formulaLabel}>{label}</span>
      <div className={css.pathTokens} data-sl-control="path-token-picker">
        {columns.map(column => (
          <button
            key={column.id}
            type="button"
            className={css.pathToken}
            onClick={() => {
              setRaw(
                JSON.stringify(
                  { type: 'path', path: ['row', column.name] },
                  null,
                  2
                )
              );
              setError(null);
            }}
          >
            row.{column.name}
          </button>
        ))}
      </div>
      <textarea
        className={css.formulaInput}
        data-sl-control="formula-editor"
        rows={4}
        value={raw}
        placeholder={placeholder}
        onChange={event => {
          setRaw(event.target.value);
          setError(null);
        }}
        onBlur={() => {
          if (!raw.trim()) {
            onChange(null);
            return;
          }
          let parsed: unknown;
          try {
            parsed = JSON.parse(raw);
          } catch (err) {
            setError(
              err instanceof Error
                ? err.message
                : t['com.streamlain.customApps.viewConfig.seo.invalidJson']()
            );
            return;
          }
          const result = FormulaSchema.safeParse(parsed);
          if (!result.success) {
            const first = result.error.issues[0];
            setError(
              first
                ? `${first.path.join('.') || 'formula'}: ${first.message}`
                : t['com.streamlain.customApps.viewConfig.seo.invalidFormula']()
            );
            return;
          }
          onChange(result.data);
        }}
      />
      {preview !== null ? (
        <span className={css.formulaPreview} data-sl-control="formula-preview">
          {preview}
        </span>
      ) : null}
      {error ? <span className={css.formulaError}>{error}</span> : null}
    </label>
  );
}
